import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import darkTheme from 'themes/darkTheme';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import ProgressHeader from '../components/ProgressHeader';
import { useSignup } from '../context/SignupContext';
import PitPayTermsContent from './PitPayTermsContent';
import TicketHossTermsContent from './TicketHossTermsContent';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
  padding: 32px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const ScrollContainer = styled.div`
  max-height: 500px;
  overflow-y: scroll;
  padding: 24px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    padding: 24px 12px;
  }

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #fa4616;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #e03a0d;
  }
`;

const TermsContent = styled.div`
  color: #94a3b8;
  font-size: 14px;
  line-height: 1.8;

  h1 {
    color: #ffffff;
    font-size: 24px;
    font-weight: 600;
    margin: 0 0 8px 0;
  }

  h3 {
    color: #ffffff;
    font-size: 16px;
    font-weight: 600;
    margin: 24px 0 12px 0;

    &.exhibit-title {
      text-align: center;
      font-size: 18px;
      margin-top: 32px;
    }
  }

  h4 {
    color: #e2e8f0;
    font-size: 14px;
    font-weight: 600;
    margin: 16px 0 8px 0;
  }

  p {
    margin: 0 0 16px 0;

    &.indent {
      margin-left: 18px;
    }

    &.indent-more {
      margin-left: 36px;
    }
  }

  strong {
    color: #ffffff;
    font-weight: 600;
  }

  .last-updated {
    font-size: 13px;
    color: #64748b;
    margin-bottom: 24px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;
    font-size: 13px;

    th,
    td {
      border: 1px solid rgba(255, 255, 255, 0.1);
      padding: 8px 12px;
      text-align: left;
    }

    th {
      background: rgba(255, 255, 255, 0.05);
      color: #ffffff;
      font-weight: 600;
    }

    td {
      color: #94a3b8;
    }

    @media (max-width: 600px) {
      thead {
        display: none;
      }

      tbody tr {
        display: block;
        margin-bottom: 6px;
        background: rgba(255, 255, 255, 0.02);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 6px;
        padding: 4px 0;
      }

      td {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border: none;
        padding: 3px 10px;
        text-align: right;

        &::before {
          content: attr(data-label);
          color: #64748b;
          font-weight: 500;
          text-align: left;
          margin-right: 8px;
        }

        &:first-child {
          display: block;
          color: #ffffff;
          font-weight: 600;
          padding-bottom: 2px;
          text-align: left;

          &::before {
            display: none;
          }
        }
      }
    }
  }

  ul {
    margin: 0 0 16px 0;
    padding-left: 24px;

    li {
      margin-bottom: 8px;
    }
  }
`;

const ScrollIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  background: ${(props) =>
    props.hasScrolled ? 'rgba(34, 197, 94, 0.1)' : 'rgba(250, 70, 22, 0.1)'};
  border: 1px solid
    ${(props) =>
      props.hasScrolled ? 'rgba(34, 197, 94, 0.2)' : 'rgba(250, 70, 22, 0.2)'};
  border-radius: 8px;
  margin-bottom: 24px;
  transition: all 0.3s;

  svg {
    width: 20px;
    height: 20px;
    color: ${(props) => (props.hasScrolled ? '#22c55e' : '#fa4616')};
    flex-shrink: 0;
  }

  span {
    color: ${(props) => (props.hasScrolled ? '#22c55e' : '#fa4616')};
    font-size: 14px;
    font-weight: 500;
    line-height: 1;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;

  button {
    width: auto;
    min-width: 140px;
    flex: 0 0 auto;
  }

  @media (max-width: 768px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`;

const TermsStep = () => {
  const history = useHistory();
  const { getTotalSteps, getStepNumber } = useSignup();
  const scrollRef = useRef(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      // Check if scrolled to bottom (with 10px threshold for precision)
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
      setHasScrolledToBottom(isAtBottom);
    }
  };

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
      // Check initial scroll position in case content is short enough
      handleScroll();
      return () => scrollElement.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const handleBack = () => {
    history.push('/promoter-signup/mailing-address');
  };

  const handleContinue = () => {
    if (hasScrolledToBottom) {
      history.push('/promoter-signup/review');
    }
  };

  const isTicketHoss = process.env.REACT_APP_PLATFORM === 'tickethoss';

  return (
    <ThemeProvider theme={darkTheme}>
      <PageContainer>
        <ContentWrapper>
          <ProgressHeader
            currentStep={getStepNumber('/promoter-signup/terms')}
            totalSteps={getTotalSteps()}
            stepTitle="Terms of Service"
          />

          <Card compact>
            <ScrollIndicator hasScrolled={hasScrolledToBottom}>
              {hasScrolledToBottom ? (
                <>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>You have read the complete Terms of Service</span>
                </>
              ) : (
                <>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 14l-7 7m0 0l-7-7m7 7V3"
                    />
                  </svg>
                  <span>Please scroll to the bottom to continue</span>
                </>
              )}
            </ScrollIndicator>

            <ScrollContainer ref={scrollRef}>
              <TermsContent>
                {isTicketHoss ? (
                  <TicketHossTermsContent />
                ) : (
                  <PitPayTermsContent />
                )}
              </TermsContent>
            </ScrollContainer>

            <ButtonRow>
              <ButtonV2 onClick={handleBack}>← Back</ButtonV2>
              <ButtonV2
                onClick={handleContinue}
                disabled={!hasScrolledToBottom}
                primary={hasScrolledToBottom}
              >
                I Accept →
              </ButtonV2>
            </ButtonRow>
          </Card>
        </ContentWrapper>
      </PageContainer>
    </ThemeProvider>
  );
};

export default TermsStep;
